import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Package, Trash, ArrowRight } from "@phosphor-icons/react";

const STATUS_ORDER = ["not_started", "in_progress", "in_review", "delivered"];
const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  in_review: "In review",
  delivered: "Delivered",
};
const STATUS_TONE = {
  delivered: "text-emerald-500",
  in_review: "text-amber-500",
  in_progress: "text-primary",
  not_started: "text-muted-foreground",
};

/**
 * Read-through modal for a deliverable. Shows title, owner, current status,
 * and lets the partner cycle the status or delete the entry.
 */
export default function DeliverableViewerDialog({ deliverable, index, open, onOpenChange, onCycleStatus, onDelete }) {
  if (!deliverable) return null;
  const status = deliverable.status || "not_started";
  const nextStatus = STATUS_ORDER[(STATUS_ORDER.indexOf(status) + 1) % STATUS_ORDER.length];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-lg" data-testid="deliverable-viewer-dialog">
        <DialogHeader className="border-b border-primary/30 pb-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary flex items-center gap-1.5">
            <Package size={11} weight="fill" /> Deliverable
            <span className={`ml-2 ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">{deliverable.title}</DialogTitle>
        </DialogHeader>

        <section className="space-y-3">
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Owner</div>
            <div className="font-serif text-sm">{deliverable.owner || "Unassigned"}</div>
          </div>
          {deliverable.due_date && (
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Due</div>
              <div className="font-serif text-sm">{deliverable.due_date}</div>
            </div>
          )}
          {deliverable.description && (
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Description</div>
              <div className="font-serif text-sm whitespace-pre-wrap">{deliverable.description}</div>
            </div>
          )}
        </section>

        <DialogFooter className="border-t border-border pt-4 gap-2 flex-wrap sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onDelete?.(index)}
            data-testid="deliverable-viewer-delete"
            className="rounded-none text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash size={14} className="mr-1.5" /> Delete
          </Button>
          <Button
            onClick={() => onCycleStatus?.(index)}
            data-testid="deliverable-viewer-cycle"
            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Advance to <span className="ml-1.5 font-mono uppercase text-[0.625rem] tracking-widest">{STATUS_LABEL[nextStatus]}</span>
            <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
